import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../supabase';
import { useActiveOrg } from '../activeOrg';
import {
  ACTIVITY_CAP,
  type ActivityEvent,
  type ActivityKind,
  type DashboardData,
  type DashboardMode,
  type PendingInvitationItem,
  type PendingWorkItem,
  type RecentDocItem
} from '../models/dashboard';

/**
 * ViewModel for `/dashboard` (turn 12.1 — route + populated-mode VM).
 *
 * Resolves the viewer's "home org" (oldest membership — the same heuristic as
 * `useMembersVM` / `useAccountVM`; a shared active-org context driven by the
 * shell org-switcher lands in turn 17.x) and fetches one aggregate payload via
 * the `get_dashboard(p_org_id)` RPC. The RPC returns the four populated-mode
 * sources in a single round-trip: Pending work, Recent documents, Activity
 * (the VM caps it at 20), and Pending invitations.
 *
 * The `documents` / `tasks` / activity tables and the `get_dashboard` RPC land
 * in Phase 3's schema turns (13.1 / 14.1 / 15.1). Until the RPC exists,
 * PostgREST answers with `PGRST202` ("function not found"); the VM treats that
 * specific case as an empty populated dashboard so the surface still renders.
 * Any other failure surfaces as `status: 'error'`.
 *
 * Populated-mode cards (turn 12.2) and onboarding mode (turn 12.3) compose on
 * top of this VM.
 *
 * The dashboard re-fetches on `visibilitychange` (a backgrounded tab regaining
 * focus refreshes its current org). Org switches — in-tab and cross-tab — are
 * owned by the shell's active org (`useActiveOrg`); the load effect re-runs
 * when it changes, so no dedicated broadcast listener lives here anymore.
 */

export type DashboardStatus = 'loading' | 'ready' | 'unauth' | 'error';

export interface UseDashboardVM {
  status: DashboardStatus;
  /** Render mode — `populated` card stack or `onboarding` checklist. */
  mode: DashboardMode;
  /** Home org id the payload was fetched for. */
  activeOrgId: string | null;
  activeOrgName: string | null;
  pendingWork: ReadonlyArray<PendingWorkItem>;
  recentDocs: ReadonlyArray<RecentDocItem>;
  /** Activity feed, capped at 20 (`ACTIVITY_CAP`). */
  activity: ReadonlyArray<ActivityEvent>;
  /** Pending invitations matched to the viewer's email. */
  pendingInvitations: ReadonlyArray<PendingInvitationItem>;
  /** Epoch ms of the last successful fetch; `null` before the first one. */
  lastRefreshedAt: number | null;
  /** True while a `refresh()` triggered re-fetch is in flight. */
  refreshing: boolean;
  error: string | null;
  /** Re-fetch the aggregate payload against the current home org. */
  refresh: () => Promise<void>;
}

interface RawPendingWork {
  id?: string | null;
  kind?: string | null;
  title?: string | null;
  doc_type?: string | null;
  status?: string | null;
  last_event_at?: string | null;
  due_at?: string | null;
}

interface RawRecentDoc {
  id?: string | null;
  doc_type?: string | null;
  title?: string | null;
  status?: string | null;
  actor_name?: string | null;
  last_event_at?: string | null;
}

interface RawActivity {
  id?: string | null;
  actor_name?: string | null;
  actor_archived?: boolean | null;
  verb?: string | null;
  subject_type?: string | null;
  subject_title?: string | null;
  subject_id?: string | null;
  kind?: string | null;
  occurred_at?: string | null;
}

interface RawInvitation {
  id?: string | null;
  token?: string | null;
  org_name?: string | null;
  role_label?: string | null;
  inviter_name?: string | null;
  invited_at?: string | null;
}

interface RawDashboard {
  mode?: string | null;
  pending_work?: RawPendingWork[] | null;
  recent_documents?: RawRecentDoc[] | null;
  activity?: RawActivity[] | null;
  pending_invitations?: RawInvitation[] | null;
}

function normalizeMode(m: string | null | undefined): DashboardMode {
  return m === 'onboarding' ? 'onboarding' : 'populated';
}

function normalizeActivityKind(k: string | null | undefined): ActivityKind {
  if (k === 'task' || k === 'member') return k;
  return 'doc';
}

function normalizePendingWork(
  raw: ReadonlyArray<RawPendingWork> | null | undefined
): PendingWorkItem[] {
  const out: PendingWorkItem[] = [];
  for (const r of raw ?? []) {
    if (!r.id) continue;
    out.push({
      id: r.id,
      kind: r.kind === 'task' ? 'task' : 'doc',
      title: r.title ?? '',
      docType: r.doc_type ?? null,
      status: r.status ?? '',
      lastEventAt: r.last_event_at ?? '',
      dueAt: r.due_at ?? null
    });
  }
  return out;
}

function normalizeRecentDocs(
  raw: ReadonlyArray<RawRecentDoc> | null | undefined
): RecentDocItem[] {
  const out: RecentDocItem[] = [];
  for (const r of raw ?? []) {
    if (!r.id) continue;
    out.push({
      id: r.id,
      docType: r.doc_type ?? '',
      title: r.title ?? '',
      status: r.status ?? '',
      actorName: r.actor_name ?? '',
      lastEventAt: r.last_event_at ?? ''
    });
  }
  return out;
}

function normalizeActivity(
  raw: ReadonlyArray<RawActivity> | null | undefined
): ActivityEvent[] {
  const out: ActivityEvent[] = [];
  for (const r of raw ?? []) {
    if (!r.id) continue;
    out.push({
      id: r.id,
      actorName: r.actor_name ?? '',
      actorArchived: r.actor_archived === true,
      verb: r.verb ?? '',
      subjectType: r.subject_type ?? '',
      subjectTitle: r.subject_title ?? '',
      subjectId: r.subject_id ?? '',
      kind: normalizeActivityKind(r.kind),
      occurredAt: r.occurred_at ?? ''
    });
  }
  // Hard cap — the feed is never longer than ACTIVITY_CAP regardless of what
  // the RPC returns.
  return out.slice(0, ACTIVITY_CAP);
}

function normalizeInvitations(
  raw: ReadonlyArray<RawInvitation> | null | undefined
): PendingInvitationItem[] {
  const out: PendingInvitationItem[] = [];
  for (const r of raw ?? []) {
    if (!r.id) continue;
    out.push({
      id: r.id,
      token: r.token ?? '',
      orgName: r.org_name ?? '',
      roleLabel: r.role_label ?? '',
      inviterName: r.inviter_name ?? '',
      invitedAt: r.invited_at ?? ''
    });
  }
  return out;
}

/** PostgREST code for "the requested function was not found". */
const FN_NOT_FOUND = 'PGRST202';

const EMPTY_DASHBOARD: DashboardData = {
  mode: 'populated',
  pendingWork: [],
  recentDocs: [],
  activity: [],
  pendingInvitations: []
};

type FetchResult =
  | { ok: true; data: DashboardData }
  /** The `get_dashboard` RPC isn't deployed yet (Phase 3 schema turns). */
  | { ok: false; reason: 'fn-missing' }
  | { ok: false; reason: 'error' };

/**
 * Fetch + normalize the aggregate payload for `orgId`. Pure of React state —
 * callers (the load effect, `refresh`) apply the result so a single fetch
 * helper can be shared without leaking cancellation across invocations.
 */
async function fetchDashboard(orgId: string): Promise<FetchResult> {
  const { data, error: rpcError } = await supabase.rpc('get_dashboard', {
    p_org_id: orgId
  });

  if (rpcError) {
    const code = (rpcError as { code?: string }).code;
    if (code === FN_NOT_FOUND) return { ok: false, reason: 'fn-missing' };
    return { ok: false, reason: 'error' };
  }

  const payload = (data ?? {}) as RawDashboard;
  return {
    ok: true,
    data: {
      mode: normalizeMode(payload.mode),
      pendingWork: normalizePendingWork(payload.pending_work),
      recentDocs: normalizeRecentDocs(payload.recent_documents),
      activity: normalizeActivity(payload.activity),
      pendingInvitations: normalizeInvitations(payload.pending_invitations)
    }
  };
}

export function useDashboardVM(): UseDashboardVM {
  const [status, setStatus] = useState<DashboardStatus>('loading');
  const [mode, setMode] = useState<DashboardMode>('populated');
  // Active org is owned by the shell (see `useActiveOrg`). The dashboard is a
  // pure consumer: it fetches for whatever org the shell says is active and
  // re-fetches when it changes — in-tab switch and cross-tab alike, since the
  // shell adopts cross-tab `switched` broadcasts into this same value.
  const { activeOrgId, activeOrgName } = useActiveOrg();
  const [pendingWork, setPendingWork] = useState<ReadonlyArray<PendingWorkItem>>(
    []
  );
  const [recentDocs, setRecentDocs] = useState<ReadonlyArray<RecentDocItem>>([]);
  const [activity, setActivity] = useState<ReadonlyArray<ActivityEvent>>([]);
  const [pendingInvitations, setPendingInvitations] = useState<
    ReadonlyArray<PendingInvitationItem>
  >([]);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<number | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mountedRef = useRef(true);
  /** Mirrors `activeOrgId` so the focus / broadcast listeners can read the
   *  current org without re-subscribing every time it changes. */
  const activeOrgIdRef = useRef<string | null>(null);
  activeOrgIdRef.current = activeOrgId;

  /** Apply a `fetchDashboard` result to component state. `refreshFailedCopy`
   *  swaps the error message for the `refresh()` path. */
  const applyResult = useCallback(
    (result: FetchResult, refreshFailedCopy = false) => {
      if (result.ok) {
        setMode(result.data.mode);
        setPendingWork(result.data.pendingWork);
        setRecentDocs(result.data.recentDocs);
        setActivity(result.data.activity);
        setPendingInvitations(result.data.pendingInvitations);
        setStatus('ready');
        setError(null);
        setLastRefreshedAt(Date.now());
        return;
      }
      if (result.reason === 'fn-missing') {
        // The `get_dashboard` RPC ships with the Phase 3 schema turns. Until
        // then PostgREST returns PGRST202 — degrade to an empty populated
        // dashboard so the surface still renders.
        setMode(EMPTY_DASHBOARD.mode);
        setPendingWork(EMPTY_DASHBOARD.pendingWork);
        setRecentDocs(EMPTY_DASHBOARD.recentDocs);
        setActivity(EMPTY_DASHBOARD.activity);
        setPendingInvitations(EMPTY_DASHBOARD.pendingInvitations);
        setStatus('ready');
        setError(null);
        setLastRefreshedAt(Date.now());
        return;
      }
      setStatus('error');
      setError(
        refreshFailedCopy
          ? "Couldn't refresh your dashboard. Please try again."
          : "Couldn't load your dashboard. Please try again."
      );
    },
    []
  );

  // Load (and re-load on org switch) the aggregate payload for the active org.
  // Re-runs whenever `activeOrgId` changes; previously this ran once on mount
  // and pinned to the oldest membership, ignoring switches.
  useEffect(() => {
    let cancelled = false;
    mountedRef.current = true;
    (async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      if (cancelled) return;
      if (!sessionData.session) {
        setStatus('unauth');
        return;
      }
      // No active org yet — the shell is still resolving which org to land in.
      // Stay loading; this effect re-runs once it resolves.
      if (!activeOrgId) return;

      const result = await fetchDashboard(activeOrgId);
      if (cancelled) return;
      applyResult(result);
    })();
    return () => {
      cancelled = true;
      mountedRef.current = false;
    };
  }, [activeOrgId, applyResult]);

  /** Re-fetch the aggregate payload for `orgId` and apply it with the
   *  refresh-failure copy. Shared by the manual `refresh()` action and the
   *  focus / org-switch triggers. */
  const fetchForOrg = useCallback(
    async (orgId: string) => {
      setRefreshing(true);
      setError(null);
      const result = await fetchDashboard(orgId);
      if (!mountedRef.current) return;
      applyResult(result, true);
      setRefreshing(false);
    },
    [applyResult]
  );

  const refresh = useCallback(async () => {
    if (!activeOrgId) return;
    await fetchForOrg(activeOrgId);
  }, [activeOrgId, fetchForOrg]);

  // Re-fetch when the tab regains focus so a backgrounded dashboard isn't
  // left showing stale data.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const orgId = activeOrgIdRef.current;
      if (orgId) void fetchForOrg(orgId);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [fetchForOrg]);

  // Org switches (in-tab and cross-tab) flow through the shell's `activeOrgId`
  // now, so the load effect above re-fetches automatically — no dedicated
  // broadcast listener needed here.

  return {
    status,
    mode,
    activeOrgId,
    activeOrgName,
    pendingWork,
    recentDocs,
    activity,
    pendingInvitations,
    lastRefreshedAt,
    refreshing,
    error,
    refresh
  };
}
